/**
 * Tienda online. La unica pantalla que ve un cliente.
 *
 * QUE REEMPLAZA
 * -------------
 * El Linktree de MANZANA MORDIDA colgaba ocho planillas de Google Sheets, una
 * por marca, que habia que retocar a mano cada vez que se movia el dolar. Esto
 * es un solo link con los precios siempre al dia, porque salen de la misma base
 * que la venta.
 *
 * NO ES UNA PANTALLA DEL SISTEMA
 * ------------------------------
 * Se abre sin sesion y sin el panel (`RUTAS_PUBLICAS` y `RUTAS_SIN_PANEL`), y no
 * usa los primitivos del ERP: aquellos estan calibrados para una herramienta
 * densa que se opera once horas por dia, y esto lo mira alguien una vez, desde
 * el celular, mientras decide si venir. La identidad es la del feed de
 * Instagram: negro sobre blanco, el logo en dos lineas con la manzana en la O, y
 * la franja de color arriba. Ver `design-system/tora/pages/catalogo.md`.
 *
 * CUATRO SECCIONES, CUATRO FORMAS
 * -------------------------------
 * Una sola grilla plana de treinta tarjetas iguales se lee como la planilla de
 * la que viene. Cada seccion tiene la forma que le sirve a su contenido: lo que
 * hay en el local y los sellados en grilla, los usados en lista (cada uno es una
 * unidad con datos que en una grilla no entran), los accesorios en grilla chica.
 *
 * Y no muestra numeros internos: ni costos, ni margenes, ni el stock exacto. La
 * defensa real esta en el backend, donde las vistas no traen esas columnas.
 */
import { useEffect, useMemo, useState, type ReactNode } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { formatear } from "@tora/shared";
import { ArrowRight } from "lucide-react";

import { FichaModelo } from "@/components/tienda/FichaModelo";
import { BarraConsulta, PanelConsulta } from "@/components/tienda/PanelConsulta";
import { Pie } from "@/components/tienda/Pie";
import {
  afueraDelHome,
  clavePropia,
  esPropia,
  gruposDeSeccion,
  idsDelHome,
  nombreEnTienda,
  ordenDeCategorias,
  ordenDeSecciones,
  seccionVisible,
  tituloDeSeccion,
  TODO_EL_CATALOGO,
} from "@/components/tienda/home";
import { FilaDeslizable } from "@/components/tienda/FilaDeslizable";
import { FiltroElegido, MenuCategorias } from "@/components/tienda/MenuCategorias";
import { useCatalogo, useConsulta, useNegocio, useOfertas } from "@/components/tienda/consultas";
import {
  agruparEnSecciones,
  agruparGeneraciones,
  cantidadDe,
  CATEGORIA_ACCESORIOS,
  categoriasDeCatalogo,
  consultaAlDia,
  cuantosProductos,
  filasDeLaGeneracion,
  filtrarCatalogo,
  linkWhatsapp,
  numeroWhatsapp,
  type GrupoGeneracion,
  type ProductoPublico,
} from "@/components/tienda/logica";
import {
  FilaUsado,
  FranjaMarca,
  IconoWhatsapp,
  Marca,
  Tarjeta,
  TarjetaModelo,
  TarjetaOferta,
  TituloSeccion,
} from "@/components/tienda/piezas";

export const Route = createFileRoute("/catalogo")({ component: Catalogo });

/** La web de Simplika, a donde lleva la firma de abajo del logo. */
const WEB_SIMPLIKA = "https://www.simplikarg.com";

/**
 * Esta pagina es blanca, aunque el telefono este en modo oscuro.
 *
 * DOS COSAS QUE NO SON LA PAGINA Y SE VEN IGUAL
 * ---------------------------------------------
 * El `body` se asoma cuando el scroll rebota en iOS, y el `theme-color` pinta
 * la barra del navegador. Los dos salen del tema del ERP, que en oscuro es casi
 * negro, y alrededor de un catalogo blanco eso se lee como que la pagina esta
 * rota o quedo angosta.
 *
 * El catalogo no tiene modo oscuro: es negro sobre blanco y ya, como el feed.
 * Asi que mientras esta abierto los fuerza, y al salir los devuelve tal como
 * estaban —no a un valor que supongamos, al que habia—.
 */
function usarPaginaBlanca(): void {
  useEffect(() => {
    const meta = document.querySelectorAll<HTMLMetaElement>('meta[name="theme-color"]');
    const antes = {
      body: document.body.style.backgroundColor,
      html: document.documentElement.style.backgroundColor,
      metas: [...meta].map((m) => m.content),
    };

    document.body.style.backgroundColor = "#FFFFFF";
    document.documentElement.style.backgroundColor = "#FFFFFF";
    meta.forEach((m) => (m.content = "#FFFFFF"));

    return () => {
      document.body.style.backgroundColor = antes.body;
      document.documentElement.style.backgroundColor = antes.html;
      meta.forEach((m, i) => (m.content = antes.metas[i] ?? m.content));
    };
  }, []);
}

function Catalogo(): JSX.Element {
  const catalogo = useCatalogo();
  const negocio = useNegocio();
  const ofertas = useOfertas();
  const { consulta, agregar, quitar, sacar } = useConsulta();

  const [texto, setTexto] = useState("");
  // `null` es el home; `TODO_EL_CATALOGO`, todo lo publicado; si no, un rubro.
  const [categoria, setCategoria] = useState<string | null>(null);
  usarPaginaBlanca();
  const [panelAbierto, setPanelAbierto] = useState(false);
  const [modeloAbierto, setModeloAbierto] = useState<GrupoGeneracion | null>(null);

  const productos = useMemo(() => catalogo.data ?? [], [catalogo.data]);
  const porId = useMemo(() => new Map(productos.map((p) => [p.id, p])), [productos]);

  // Las categorias salen del catalogo entero y NO de lo filtrado: si se
  // recalcularan sobre el filtro, elegir una dejaria una sola en la barra y no
  // habria como volver.
  const categorias = useMemo(() => categoriasDeCatalogo(productos), [productos]);
  // Las tarjetas, no las filas: el menu decia «Todo 107» y este boton «142»
  // (cada memoria de un iPhone es una fila y una sola tarjeta).
  const tarjetasDelCatalogo = categorias.reduce((n, c) => n + c.cuantos, 0);

  // EL HOME (0093). Sin buscar ni elegir categoria se ve solo lo marcado para
  // el home; buscando o eligiendo una, todo lo publicado. Lo que no esta en el
  // home se encuentra: por eso buscar mira siempre el catalogo entero.
  //
  // Las SECCIONES PROPIAS (0104) las arma el local a mano, mezclando rubros, y
  // en el home sus productos salen de la seccion automatica: el mismo iPhone
  // dos veces en la misma pantalla se lee como un error. Buscando o por
  // categoria, cada cosa vuelve a su seccion. Una seccion apagada no va al
  // home, pero lo que tiene sigue publicado.
  const idsHome = useMemo(() => idsDelHome(productos), [productos]);
  const afuera = useMemo(() => afueraDelHome(productos), [productos]);
  const propias = useMemo(
    () =>
      (negocio.data?.secciones_propias ?? []).map((s) => ({
        ...s,
        grupos: gruposDeSeccion(productos, s.productos),
      })),
    [negocio.data, productos],
  );
  const idsEnPropias = useMemo(() => new Set(propias.flatMap((s) => s.productos)), [propias]);
  const algunaApagada = (negocio.data?.secciones_home ?? []).some((c) => c.visible === false);
  const recortado =
    idsHome.size < productos.length || propias.some((s) => s.grupos.length > 0) || algunaApagada;
  const enElHome = categoria === null && texto.trim() === "" && recortado;

  // El buscador mira tambien el nombre que el local le puso a cada tarjeta:
  // si la tarjeta dice «AirPods Pro 3», buscar eso tiene que encontrarla.
  const nombresTienda = negocio.data?.nombres_tienda ?? null;
  const filtrados = useMemo(() => {
    const rubro = categoria === TODO_EL_CATALOGO ? null : categoria;
    const todos = filtrarCatalogo(productos, texto, rubro, nombresTienda);
    return enElHome ? todos.filter((p) => idsHome.has(p.id) && !idsEnPropias.has(p.id)) : todos;
  }, [productos, texto, categoria, enElHome, idsHome, idsEnPropias, nombresTienda]);

  /** Salir del home a una categoria, desde el pie de una seccion. */
  function verCategoria(slug: string): void {
    setCategoria(slug);
    window.scrollTo({ top: 0, behavior: "smooth" });
  }

  const secciones = useMemo(() => agruparEnSecciones(filtrados), [filtrados]);

  // Los equipos se muestran por GENERACION: el 18 con sus tres variantes y sus
  // memorias es una sola tarjeta, aunque en la base sean seis productos. El
  // cliente llega buscando "el 18"; lo demas lo elige adentro. Los accesorios no
  // se agrupan: cada uno es lo que es.
  const enLocal = useMemo(() => agruparGeneraciones(secciones.enLocal), [secciones.enLocal]);
  // Una seccion por rubro, y adentro de cada una los iPhone agrupados por
  // generacion. Antes era una sola grilla "Sellados" con treinta y ocho
  // tarjetas de cosas que no se comparan entre si: un Mac al lado de un Watch.
  const porRubro = useMemo(
    () => secciones.porRubro.map((r) => ({ ...r, grupos: agruparGeneraciones(r.productos) })),
    [secciones.porRubro],
  );

  // Los usados se agrupan igual que los sellados. Sin agrupar eran diecinueve
  // renglones casi iguales —"iPhone 13 128", "iPhone 13 256", "iPhone 13 Pro
  // 128"…— que hay que leer enteros para encontrar uno; agrupados son cuatro
  // tarjetas, una por generacion.
  //
  // Una UNIDAD no entra a la grilla: lo que la vende es su ficha —bateria 89 %,
  // color real— y una tarjeta de generacion la esconde. Va abajo, en lista.
  const usadosModelo = useMemo(
    () => agruparGeneraciones(secciones.usados.filter((p) => !p.es_unidad)),
    [secciones.usados],
  );
  const usadosUnidad = useMemo(
    () => secciones.usados.filter((p) => p.es_unidad),
    [secciones.usados],
  );

  const n = negocio.data;
  // Lo acomodado a mano desde /tienda (0095): el titulo de cada seccion, su
  // orden y el nombre de cada tarjeta. Sin cargar, es lo de siempre.
  const cfgSecciones = n?.secciones_home ?? null;
  const titulo = (clave: string, deSiempre: string): string =>
    tituloDeSeccion(clave, deSiempre, cfgSecciones);
  // Cada categoria con el nombre que le puso el local (Apple se llama iPhones).
  // La de accesorios junta los que vienen de otros rubros, y se llama como la
  // seccion Accesorios del home.
  // Y en el orden de las secciones del home (`ordenDeCategorias`).
  const categoriasDelMenu = ordenDeCategorias(categorias, cfgSecciones).map((c) => ({
    ...c,
    nombre: titulo(
      `rubro:${c.slug}`,
      c.slug === CATEGORIA_ACCESORIOS ? titulo("accesorios", c.nombre) : c.nombre,
    ),
  }));
  const conNombre = (g: GrupoGeneracion): GrupoGeneracion => ({
    ...g,
    nombre: nombreEnTienda(g, n?.nombres_tienda),
  });
  const nombreDe = (p: ProductoPublico): string | undefined =>
    n?.nombres_tienda?.[`#${p.id}`]?.trim() || undefined;
  // Las ofertas no se filtran con el buscador: no son productos del catalogo,
  // son lo que el local esta ofreciendo. Pero buscando o con una categoria
  // elegida no se muestran: quedaban arriba de los resultados y con el teclado
  // abierto en el telefono no se veia ni uno, parecia que buscar no hacia nada.
  const listaOfertas = ofertas.data ?? [];
  const conOfertas = listaOfertas.length > 0 && texto.trim() === "" && categoria === null;
  const whatsapp = numeroWhatsapp(n?.whatsapp);
  const linkGeneral = linkWhatsapp(whatsapp, "Hola! Te escribo por el catálogo.");
  // Lo que se busco y no estaba, en el mensaje: si no, el local tiene que
  // preguntar que buscaba.
  const linkNoEsta = texto.trim()
    ? linkWhatsapp(whatsapp, `Hola! Busco «${texto.trim()}» y no lo encontré en el catálogo.`)
    : linkGeneral;

  const precio = (p: ProductoPublico): string => formatear(Number(p.precio_ars) || 0, "ARS");
  const conProducto = (p: ProductoPublico) => ({
    cantidad: cantidadDe(consulta, p.id),
    onAgregar: () => agregar(p),
    onQuitar: () => quitar(p.id),
    precio: precio(p),
  });

  // La consulta con el precio de hoy, y aparte lo que ya no se publica. Sin el
  // catalogo cargado no se juzga nada (`null`).
  const alDia = useMemo(
    () => consultaAlDia(consulta, catalogo.isSuccess ? porId : null),
    [consulta, catalogo.isSuccess, porId],
  );

  const hayAlgo =
    enLocal.length + porRubro.length + secciones.usados.length + secciones.accesorios.length > 0;

  return (
    // `overflow-x-hidden` es la red, no el arreglo: las filas que corren de
    // costado tienen su propio scroll y siguen andando, pero si a alguna se le
    // escapa un pixel la pagina ENTERA deja de correrse en el telefono. Pasaba:
    // cuatro pixeles de mas y el encabezado quedaba descentrado al deslizar.
    <div className="min-h-[100dvh] overflow-x-hidden bg-white text-[#0A0A0A]">
      <FranjaMarca />

      {/* En la PC la pagina no se estira: arriba de 1200 px una grilla de
          productos deja de leerse como vidriera y pasa a ser una planilla. El
          `md:px-3` suma al `px-5` de cada seccion un margen de escritorio. */}
      <div className="mx-auto w-full max-w-[1200px] md:px-3 lg:px-5">
        {/* ── Quien sos ── */}
        {/* EL HERO ES EL LOGO
            Todo centrado, tambien en el telefono. Hubo un titulo y una bajada
            debajo del logo ("Apple sellado y usado, con garantia"); el local
            los saco: el logo solo se lee mejor y el buscador sube. El logo es
            el h1 de la pagina, con su alt como nombre. */}
        <header className="relative px-5 pt-7 text-center md:pt-12">
          {/* Las categorias, arriba a la izquierda como en cualquier tienda.
              Con una sola no hay nada que elegir, salvo que el home este
              recortado: ahi "Todo" es la unica forma de ver el resto. */}
          {categoriasDelMenu.length >= 2 || recortado ? (
            <div className="absolute left-2 top-4 md:left-3 md:top-6">
              <MenuCategorias
                categorias={categoriasDelMenu}
                elegida={categoria}
                onElegir={setCategoria}
                conInicio={recortado}
              />
            </div>
          ) : null}

          <h1>
            <Marca logoUrl={n?.logo_url} />
          </h1>
          {/* La firma de quien hizo la tienda: chica y gris, para que no
              compita con el logo del local. */}
          <p className="mt-2 text-center text-xs text-[#6E6E6E]">
            creado por{" "}
            <a
              href={WEB_SIMPLIKA}
              target="_blank"
              rel="noopener"
              className="font-semibold text-[#0A0A0A] underline-offset-2 hover:underline"
            >
              Simplika
            </a>
          </p>

          {/* En el telefono van uno abajo del otro, a lo ancho; en la PC, en una
            fila y centrados, con el buscador primero: con mouse y teclado se
            busca, y WhatsApp queda a mano al costado. */}
          <div className="mt-5 md:mx-auto md:mt-8 md:flex md:max-w-[640px] md:flex-row-reverse md:gap-3">
            {linkGeneral ? (
              <a
                href={linkGeneral}
                target="_blank"
                rel="noreferrer"
                className="mt-4 flex h-12 items-center justify-center gap-2 rounded-md bg-[#0A0A0A] text-sm font-semibold text-white transition-colors hover:bg-[#2A2A2A] md:mt-0 md:shrink-0 md:px-6"
              >
                <IconoWhatsapp className="size-[17px]" />
                Escribinos
              </a>
            ) : null}

            <label className="mt-3 flex h-12 items-center gap-2.5 rounded-md border border-[#E0E0E0] px-3.5 focus-within:border-[#0A0A0A] md:mt-0 md:flex-1">
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="#6E6E6E"
                strokeWidth="1.75"
                strokeLinecap="round"
                aria-hidden="true"
                className="size-[17px] shrink-0"
              >
                <circle cx="11" cy="11" r="7" />
                <path d="m20 20-3.5-3.5" />
              </svg>
              <input
                value={texto}
                onChange={(e) => setTexto(e.target.value)}
                placeholder="Buscar producto"
                aria-label="Buscar en el catálogo"
                // El borde del recuadro ya marca el foco: el anillo global de
                // `:focus-visible` dibujaba un segundo marco adentro.
                className="min-w-0 flex-1 bg-transparent text-base outline-none placeholder:text-[#6E6E6E] focus-visible:ring-0 focus-visible:ring-offset-0"
              />
            </label>
          </div>

          {categoria !== null ? (
            <div className="mt-4 flex md:mt-5">
              <FiltroElegido
                nombre={
                  categoria === TODO_EL_CATALOGO
                    ? "Todo"
                    : (categoriasDelMenu.find((c) => c.slug === categoria)?.nombre ?? "Categoría")
                }
                onQuitar={() => setCategoria(null)}
              />
            </div>
          ) : null}
        </header>

        {/* ── Ofertas ──
          Va arriba de todo porque es lo primero que se lee, y es la unica parte
          de la pantalla donde el local habla en vez de enumerar. Se desplaza de
          costado: son pocas y compiten entre si, no con el catalogo. */}
        {conOfertas ? (
          <section className="pt-8 md:pt-12">
            <div className="px-5">
              <TituloSeccion>Ofertas</TituloSeccion>
            </div>
            {/* El `px-5` adentro del carrusel —y NO un margen negativo— deja que
              la ultima tarjeta llegue al borde. La seccion no tiene padding
              lateral, asi que un `-mx-5` la sacaba 20 px fuera de la pantalla y
              la pagina ENTERA se corria para el costado en el telefono. */}
            {/* En la PC no corre de costado: con el mouse una fila sin barra no se
              puede deslizar, y las que no entran quedarian escondidas. */}
            <div className="flex gap-2.5 overflow-x-auto px-5 pb-1 [scrollbar-width:none] md:flex-wrap md:gap-3.5 md:overflow-visible [&::-webkit-scrollbar]:hidden">
              {listaOfertas.map((o) => (
                <TarjetaOferta
                  key={o.id}
                  oferta={o}
                  whatsapp={n?.whatsapp ?? null}
                  ancha={listaOfertas.length === 1}
                  producto={
                    o.codigo ? (productos.find((p) => p.codigo_interno === o.codigo) ?? null) : null
                  }
                />
              ))}
            </div>
          </section>
        ) : null}

        {/* ── Las secciones, en el orden del home ──
          Cada una es un bloque con su clave, y se dibujan en el orden que el
          local acomodo en /tienda (`ordenDeSecciones`). Las que no toco van
          en el de siempre. */}
        {ordenDeSecciones(
          [
            ...propias.map((s) => clavePropia(s.id)),
            "en-local",
            ...porRubro.map((r) => `rubro:${r.slug}`),
            "usados",
            "accesorios",
          ],
          cfgSecciones,
        ).map((clave) => {
          const grilla =
            "grid grid-cols-2 gap-x-3.5 gap-y-6 md:grid-cols-3 md:gap-x-5 md:gap-y-9 lg:grid-cols-4 xl:grid-cols-5";
          // Sin buscar ni elegir categoria, cada seccion es una fila que corre
          // de costado (`FilaDeslizable`); buscando o en una categoria, grilla.
          const enFila = categoria === null && texto.trim() === "";
          const conjunto = (nombre: string, hijos: ReactNode, chico = false): ReactNode =>
            enFila ? (
              <FilaDeslizable etiqueta={nombre} chico={chico}>
                {hijos}
              </FilaDeslizable>
            ) : (
              <div
                className={
                  chico
                    ? "grid grid-cols-3 gap-x-2.5 gap-y-5 md:grid-cols-4 md:gap-x-4 md:gap-y-7 lg:grid-cols-6"
                    : grilla
                }
              >
                {hijos}
              </div>
            );
          const tarjetas = (grupos: GrupoGeneracion[]) =>
            grupos.map((g) => (
              <TarjetaModelo
                key={g.clave}
                grupo={conNombre(g)}
                filas={filasDeLaGeneracion(g, productos)}
                precioDesde={formatear(g.desde, "ARS")}
                onAbrir={() => setModeloAbierto(conNombre(g))}
              />
            ));

          // En el home, una seccion apagada no se dibuja.
          if (enElHome && !seccionVisible(clave, cfgSecciones)) return null;

          if (esPropia(clave)) {
            const propia = propias.find((s) => clavePropia(s.id) === clave);
            // Solo en el home: buscando o por categoria, cada producto esta
            // en su seccion de siempre.
            return enElHome && propia && propia.grupos.length > 0 ? (
              <section key={clave} className="px-5 pt-9 md:pt-14">
                <TituloSeccion>{propia.titulo}</TituloSeccion>
                {conjunto(propia.titulo, tarjetas(propia.grupos))}
              </section>
            ) : null;
          }

          if (clave === "en-local") {
            return enLocal.length > 0 ? (
              <section key={clave} className="px-5 pt-9 md:pt-14">
                <TituloSeccion bajada="Te lo llevás hoy." contador={enLocal.length}>
                  {titulo(clave, "En stock")}
                </TituloSeccion>
                {conjunto(titulo(clave, "En stock"), tarjetas(enLocal))}
              </section>
            ) : null;
          }

          if (clave === "usados") {
            return secciones.usados.length > 0 ? (
              <section key={clave} className="px-5 pt-9 md:pt-14">
                <TituloSeccion
                  bajada="Revisados y con garantía. La batería y el estado de cada uno, antes de que decidas."
                  contador={secciones.usados.length}
                >
                  {titulo(clave, "Usados")}
                </TituloSeccion>

                {/* En grilla y por generacion, igual que los sellados. Las
                  UNIDADES van abajo en lista: lo que las vende es la ficha, y
                  una tarjeta la esconde. */}
                {usadosModelo.length > 0
                  ? conjunto(titulo(clave, "Usados"), tarjetas(usadosModelo))
                  : null}

                {usadosUnidad.length > 0 ? (
                  <div className={usadosModelo.length > 0 ? "mt-8" : undefined}>
                    {usadosModelo.length > 0 ? (
                      <h3 className="mb-1 text-[15px] font-[700] tracking-[-0.02em]">
                        Unidades con ficha
                      </h3>
                    ) : null}
                    <div className="lg:grid lg:grid-cols-2 lg:gap-x-10">
                      {usadosUnidad.map((p) => (
                        <FilaUsado
                          key={p.id}
                          producto={p}
                          nombre={nombreDe(p)}
                          enConsulta={cantidadDe(consulta, p.id) > 0}
                          onAgregar={() => agregar(p)}
                          onQuitar={() => sacar(p.id)}
                          precio={precio(p)}
                        />
                      ))}
                    </div>
                  </div>
                ) : null}
              </section>
            ) : null;
          }

          if (clave === "accesorios") {
            return secciones.accesorios.length > 0 ? (
              <section key={clave} className="px-5 pt-9 md:pt-14">
                <TituloSeccion contador={secciones.accesorios.length}>
                  {titulo(clave, "Accesorios")}
                </TituloSeccion>
                {conjunto(
                  titulo(clave, "Accesorios"),
                  secciones.accesorios.map((p) => (
                    <Tarjeta
                      key={p.id}
                      producto={p}
                      nombre={nombreDe(p)}
                      chico
                      {...conProducto(p)}
                    />
                  )),
                  true,
                )}
              </section>
            ) : null;
          }

          // Un rubro, una seccion.
          const r = porRubro.find((x) => `rubro:${x.slug}` === clave);
          if (!r) return null;
          const nombreRubro = titulo(clave, r.nombre);
          return (
            <section key={clave} className="px-5 pt-9 md:pt-14">
              <TituloSeccion
                bajada={n?.dias_a_pedido ? `Llegan en ${n.dias_a_pedido} días.` : undefined}
                contador={r.grupos.length}
              >
                {nombreRubro}
              </TituloSeccion>
              {conjunto(nombreRubro, tarjetas(r.grupos))}
              {enElHome && (afuera.get(clave) ?? 0) > 0 ? (
                <VerTodo onClick={() => verCategoria(r.slug)}>Ver todo {nombreRubro}</VerTodo>
              ) : null}
            </section>
          );
        })}

        {/* ── El pie del home ──
          Lo que no esta en el home existe: esto lo dice, y lleva ahi. Tambien
          sostiene un home sin nada marcado, que sin esto seria una pagina vacia
          con un buscador. */}
        {enElHome ? (
          <div className="px-5 pt-12 text-center md:pt-16">
            <button
              type="button"
              onClick={() => verCategoria(TODO_EL_CATALOGO)}
              className="inline-flex h-12 items-center gap-2 rounded-md border border-[#0A0A0A] px-6 text-sm font-semibold transition-colors hover:bg-[#0A0A0A] hover:text-white"
            >
              Ver todo el catálogo · {tarjetasDelCatalogo}
            </button>
          </div>
        ) : null}

        {/* ── Vacio ── */}
        {/* Si el catalogo no cargo, decirlo: antes se leia «No encontramos nada
            con «funda»», como si no hubiera fundas. */}
        {catalogo.isError ? (
          <div className="px-5 py-16 text-center">
            <p className="text-sm text-[#5C5C5C]">No pudimos cargar el catálogo.</p>
            <button
              type="button"
              onClick={() => void catalogo.refetch()}
              className="mt-4 inline-flex h-11 items-center rounded-md border border-[#0A0A0A] px-5 text-sm font-semibold"
            >
              Reintentar
            </button>
          </div>
        ) : !hayAlgo && !enElHome && !catalogo.isLoading ? (
          <div className="px-5 py-16 text-center">
            <p className="text-sm text-[#5C5C5C]">
              {texto.trim()
                ? `No encontramos nada con «${texto.trim()}».`
                : "Todavía no hay productos publicados."}
            </p>
            {linkNoEsta ? (
              <a
                href={linkNoEsta}
                target="_blank"
                rel="noreferrer"
                className="mt-4 inline-flex h-11 items-center gap-2 rounded-md bg-[#0A0A0A] px-5 text-sm font-semibold text-white"
              >
                <IconoWhatsapp className="size-4" />
                Consultanos
              </a>
            ) : null}
          </div>
        ) : null}
      </div>

      <Pie
        negocio={n}
        linkGeneral={linkGeneral}
        linkCanje={linkWhatsapp(
          whatsapp,
          "Hola! Quiero entregar mi equipo como parte de pago. Tengo un: ",
        )}
        categorias={categoriasDelMenu}
        onCategoria={verCategoria}
        conBarra={cuantosProductos(consulta) > 0}
      />

      <FichaModelo
        grupo={modeloAbierto}
        catalogo={productos}
        onCerrar={() => setModeloAbierto(null)}
        onAgregar={(p, color) => agregar(p, color)}
      />

      <BarraConsulta
        consulta={consulta}
        vigentes={alDia.vigentes}
        onAbrir={() => setPanelAbierto(true)}
      />
      <PanelConsulta
        abierto={panelAbierto}
        onAbiertoChange={setPanelAbierto}
        consulta={alDia.vigentes}
        noDisponibles={alDia.noDisponibles}
        porId={porId}
        whatsapp={n?.whatsapp ?? null}
        onAgregar={(id) => {
          const p = porId.get(id);
          if (p) agregar(p);
        }}
        onQuitar={quitar}
        onSacar={sacar}
      />
    </div>
  );
}

/** El pie de una seccion recortada del home: lleva a su categoria completa. */
function VerTodo({ onClick, children }: { onClick: () => void; children: ReactNode }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="mt-5 inline-flex items-center gap-1 text-sm font-semibold underline-offset-4 hover:underline"
    >
      {children}
      <ArrowRight className="size-4" aria-hidden="true" />
    </button>
  );
}
